import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { TOUR_STEPS, subscribeTour, tourState, type TourState } from '@/tour/state';
import { partnerMatchCount, type RuleSet } from '@airmail/engine';
import { useCompetition, useTurnier } from '@/store/TournamentProvider';
import { Button, Field, Input, SectionHead, Select, Card, cx } from '@/ui';
import { RuleEditor } from '@/components/RuleEditor';
import { DEFAULT_MODE_OPTIONS, KO_TEAM_CHOICES, MODES_GEORDNET, groupSizeChoices, modeInfo, modeOfPhases, phaseConfigsFor, previewMode, type ModeKey, type ModeOptions } from '@/lib/modes';
import { fmtDateInput, fmtMinutes } from '@/lib/format';
import { phaseLabel } from '@/layout/AppShell';
import { SyncPanel } from '@/components/SyncPanel';
import { RegistrationPanel } from '@/components/RegistrationPanel';
import { MembersPanel } from '@/components/MembersPanel';
import { useT } from '@/i18n';

/**
 * Datum des Turniers — gespeichert beim Verlassen des Felds wie bisher, darunter
 * ausgeschrieben in der Sprache der App (das Feld selbst folgt dem Browser).
 */
function DatumFeld({ wert, onSpeichern }: { wert: string | undefined; onSpeichern: (date: string) => void }) {
  const t = useT();
  const [v, setV] = useState(wert ?? '');
  return (
    <Field label={t('Datum')} hint={fmtDateInput(v) || undefined}>
      <Input type="date" value={v} onChange={(e) => setV(e.target.value)} onBlur={(e) => e.target.value !== (wert ?? '') && onSpeichern(e.target.value)} />
    </Field>
  );
}

type TabKey = 'turnier' | 'regeln' | 'cloud' | 'anmeldung';
const TABS: { key: TabKey; label: string; hint: string }[] = [
  { key: 'turnier', label: 'Turnier & Modus', hint: 'Name, Datum, Ort — und wie gespielt wird (Gruppen, KO, Liga-Modus).' },
  { key: 'regeln', label: 'Regeln', hint: 'Punktziel, Bust, Sätze, Zeitlimit, Tiebreaker. Gelten für alle Spiele des Turniers.' },
  { key: 'cloud', label: 'Cloud & Helfer', hint: 'Sicherung im Konto, Helfer-Link für weitere Geräte, Zuschauer-Link, Mitorganisatoren.' },
  { key: 'anmeldung', label: 'Anmeldung', hint: 'Online-Anmeldung per Link oder QR-Code, Anmeldeschluss, Plakat und Flyer.' },
];
const TOUR_TAB: Record<string, TabKey> = { 'settings-sync': 'cloud', 'settings-members': 'cloud', 'settings-registration': 'anmeldung', 'nav-einstellungen': 'turnier', 'hdr-backup': 'turnier' };

/** Phasenstatus aus der Engine in Worten — vorher stand hier roh `planned`/`running` in allen drei Sprachen */
const PHASE_STATUS: Record<string, string> = { planned: 'geplant', drawn: 'ausgelost', running: 'läuft', finished: 'abgeschlossen' };

export function SettingsPage() {
  const t = useT();
  const { tournament, dispatch, notify } = useTurnier();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab: TabKey = tabParam === 'regeln' || tabParam === 'cloud' || tabParam === 'anmeldung' ? tabParam : 'turnier';
  const setTab = useCallback((k: TabKey) => setParams(k === 'turnier' ? {} : { tab: k }, { replace: true }), [setParams]);
  // Einführung springt auf den Reiter, auf dem ihr Ziel liegt
  useEffect(() => {
    const apply = (st: TourState) => {
      const target = st.active ? TOUR_STEPS[st.index]?.target : undefined;
      const wanted = target ? TOUR_TAB[target] : undefined;
      if (wanted && wanted !== tab) setTab(wanted);
    };
    apply(tourState());
    return subscribeTour(apply);
  }, [tab, setTab]);
  const comp = useCompetition();
  const [rules, setRules] = useState<RuleSet>(comp.ruleSet);
  const rulesDirty = JSON.stringify(rules) !== JSON.stringify(comp.ruleSet);
  const locked = comp.phases.some((p) => p.status !== 'planned');
  /*
   * Auch nach der Auslosung änderbar (Daniel, 10/2026) — über `phase.setOptions`,
   * das die Auslosung nicht verwirft: Weiter je Gruppe und beste Nächstplatzierte,
   * bis das KO ausgelost ist („falls man nachnominieren muss"); Spiel um Platz 3,
   * bis das KO abgeschlossen ist („wenn es zu lange dauert").
   */
  const gruppenPhase = comp.phases.find((p) => p.type === 'groups');
  const koPhase = comp.phases.find((p) => p.type === 'single_elim' || p.type === 'ko_rounds');
  const nachGruppen = gruppenPhase ? comp.phases.find((p) => p.order === gruppenPhase.order + 1) : undefined;
  const qualiOffen = !locked || (!!gruppenPhase && (!nachGruppen || nachGruppen.status === 'planned'));
  const platz3Offen = !locked || (!!koPhase && koPhase.status !== 'finished');
  const setzeQuali = (patch: { qualifiersPerGroup?: number; bestRemaining?: number }, neu: Partial<ModeOptions>) => {
    setOpts({ ...opts, ...neu });
    if (locked && gruppenPhase) dispatch({ type: 'phase.setOptions', phaseId: gruppenPhase.id, patch });
  };
  const setzePlatz3 = (an: boolean) => {
    setOpts({ ...opts, thirdPlaceMatch: an });
    if (locked && koPhase) dispatch({ type: 'phase.setOptions', phaseId: koPhase.id, patch: { thirdPlaceMatch: an } });
  };

  const currentMode = modeOfPhases(comp.phases.map((p) => p.config));
  const [mode, setMode] = useState<ModeKey>(currentMode === 'custom' ? 'groups_ko' : currentMode);
  const groupsCfg = comp.phases.find((p) => p.type === 'groups')?.config;
  const koCfg = comp.phases.find((p) => p.type === 'single_elim')?.config;
  const [opts, setOpts] = useState<ModeOptions>({
    groupSize: groupsCfg?.type === 'groups' && 'groupSize' in groupsCfg.layout ? groupsCfg.layout.groupSize : DEFAULT_MODE_OPTIONS.groupSize,
    qualifiersPerGroup: groupsCfg?.type === 'groups' && (currentMode === 'groups_ko' || currentMode === 'partner_ko') ? groupsCfg.qualifiersPerGroup || 2 : DEFAULT_MODE_OPTIONS.qualifiersPerGroup,
    bestRemaining: groupsCfg?.type === 'groups' ? groupsCfg.bestRemaining : DEFAULT_MODE_OPTIONS.bestRemaining,
    koTeams: groupsCfg?.type === 'groups' && currentMode === 'rr_ko' ? groupsCfg.qualifiersPerGroup || 8 : DEFAULT_MODE_OPTIONS.koTeams,
    thirdPlaceMatch: koCfg?.type === 'single_elim' ? koCfg.thirdPlaceMatch : DEFAULT_MODE_OPTIONS.thirdPlaceMatch,
    avoidSameClub: groupsCfg?.type === 'groups' ? groupsCfg.constraints.avoidSameClub : DEFAULT_MODE_OPTIONS.avoidSameClub,
    leistungsgruppen: groupsCfg?.type === 'groups' ? !!groupsCfg.groupByTeamNumber : DEFAULT_MODE_OPTIONS.leistungsgruppen,
    partners: groupsCfg?.type === 'groups' && groupsCfg.partners ? groupsCfg.partners : DEFAULT_MODE_OPTIONS.partners,
  });
  const teamCount = comp.teams.filter((t) => t.status === 'registered' || t.status === 'confirmed').length;
  const clubSizes = Object.values(
    comp.teams.filter((t) => t.status === 'registered' || t.status === 'confirmed').reduce<Record<string, number>>((acc, t) => ({ ...acc, [t.club ?? '']: (acc[t.club ?? ''] ?? 0) + 1 }), {}),
  );
  const preview = previewMode(
    mode,
    opts,
    teamCount || 16,
    tournament.courts.filter((c) => c.active).length,
    rules.plannedMatchMinutes,
    mode === 'club_rr' && clubSizes.length > 1 ? clubSizes : undefined,
    mode === 'club_rr' ? comp.teams : undefined,
  );

  /**
   * Modus übernehmen.
   *
   * Passt die Abfolge der Phasen schon (gleiche Typen in gleicher Zahl), werden
   * nur die Einstellungen ersetzt. Sonst wird sie neu aufgebaut — und genau das
   * fehlte bisher: Beim Wechsel von „Gruppen → KO" auf den Liga-Modus blieb
   * die KO-Phase übrig, und die App sagte nur „Phasen löschen kommt in Sprint
   * 4." Seitdem es `phase.remove` gibt, räumt sie wirklich auf.
   *
   * Phasen, die schon laufen oder Spiele kennen, rührt das nicht an — dort wäre
   * ein Moduswechsel ein Datenverlust, deshalb sagt die App, was im Weg steht.
   */
  const applyMode = () => {
    const wanted = phaseConfigsFor(mode, opts);
    const passt = comp.phases.length === wanted.length && comp.phases.every((p, i) => p.type === wanted[i]?.type);
    if (passt) {
      wanted.forEach((cfg, i) => dispatch({ type: 'phase.updateConfig', phaseId: (comp.phases[i] as (typeof comp.phases)[number]).id, config: cfg }, { silent: i < wanted.length - 1 }));
      return;
    }
    const laeuft = comp.phases.filter((p) => p.status !== 'planned' || p.matches.length > 0);
    if (laeuft.length > 0) {
      notify('warn', t('Der Modus lässt sich nicht mehr umstellen: {n} Phase(n) sind schon ausgelost oder gespielt. Einzelne Einstellungen gehen weiter.', { n: laeuft.length }));
      return;
    }
    for (const p of comp.phases) dispatch({ type: 'phase.remove', phaseId: p.id }, { silent: true });
    wanted.forEach((cfg, i) => dispatch({ type: 'phase.add', competitionId: comp.id, config: cfg }, { silent: i < wanted.length - 1 }));
  };

  return (
    <div className="max-w-4xl grid gap-6">
      <nav className="flex border-b border-line overflow-x-auto" aria-label={t('Einstellungen')} role="tablist">
        {TABS.map((tb) => (
          <button
            key={tb.key}
            role="tab"
            aria-selected={tab === tb.key}
            onClick={() => setTab(tb.key)}
            className={cx('px-4 py-2.5 text-sm cursor-pointer border-b-2 -mb-px whitespace-nowrap', tab === tb.key ? 'border-red text-ink font-bold' : 'border-transparent text-muted hover:text-ink')}
          >
            {t(tb.label)}
          </button>
        ))}
      </nav>
      <p className="m-0 -mt-3 text-sm text-muted">{t(TABS.find((tb) => tb.key === tab)?.hint ?? '')}</p>

      <section hidden={tab !== 'turnier'}>
        <SectionHead title={t('Turnier')} />
        <div className="field-row grid grid-cols-1 sm:grid-cols-4 gap-4">
          <Field label={t('Name')}>
            <Input
              defaultValue={tournament.name}
              onBlur={(e) => e.target.value.trim() && e.target.value !== tournament.name && dispatch({ type: 'tournament.update', patch: { name: e.target.value.trim() } })}
            />
          </Field>
          <DatumFeld wert={tournament.date} onSpeichern={(date) => dispatch({ type: 'tournament.update', patch: { date } })} />
          <Field label={t('Ort')}>
            <Input
              defaultValue={tournament.location ?? ''}
              onBlur={(e) => (e.target.value.trim() || undefined) !== tournament.location && dispatch({ type: 'tournament.update', patch: { location: e.target.value.trim() || undefined } })}
            />
          </Field>
          <Field label={t('Startgeld je Team (€)')} hint={t('Leer = kein Startgeld')}>
            <Input
              type="number"
              min={0}
              step={0.5}
              defaultValue={tournament.entryFee ?? ''}
              onBlur={(e) => {
                const raw = e.target.value.trim();
                const fee = raw === '' ? undefined : Number(raw);
                if (fee !== undefined && (!Number.isFinite(fee) || fee < 0)) return;
                if (fee !== tournament.entryFee) dispatch({ type: 'tournament.update', patch: { entryFee: fee } });
              }}
            />
          </Field>
        </div>
      </section>

      <section hidden={tab !== 'cloud'}>
        <SectionHead title={t('Cloud & Helfer')} />
        <div className="grid gap-4">
          <div data-tour="settings-sync">
            <SyncPanel />
          </div>
          <div data-tour="settings-members">
            <MembersPanel />
          </div>
        </div>
      </section>

      <section hidden={tab !== 'anmeldung'} data-tour="settings-registration">
        <SectionHead title={t('Anmeldung')} />
        <RegistrationPanel />
      </section>

      <section hidden={tab !== 'turnier'}>
        <SectionHead title={t('Modus')} meta={locked ? t('Auslosung existiert — Weiter je Gruppe, Nächstplatzierte und Spiel um Platz 3 bleiben änderbar') : undefined}>
          {!locked && (
            <Button variant="primary" onClick={applyMode}>
              {t('Modus übernehmen')}
            </Button>
          )}
        </SectionHead>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-4">
          <div className="grid gap-3">
            <Field label={t('Modus')} hint={t(modeInfo(mode).kurz)}>
              <Select value={mode} disabled={locked} onChange={(e) => setMode(e.target.value as ModeKey)}>
                {MODES_GEORDNET.map((m) => (
                  <option key={m.key} value={m.key}>
                    {t(m.name)}
                  </option>
                ))}
              </Select>
            </Field>
            {mode === 'rr_ko' && (
              <Field label={t('Weiter ins KO')}>
                <Select value={opts.koTeams} disabled={!qualiOffen} onChange={(e) => setzeQuali({ qualifiersPerGroup: Number(e.target.value) }, { koTeams: Number(e.target.value) })}>
                  {KO_TEAM_CHOICES.map((n) => (
                    <option key={n} value={n}>
                      {t('Top {n}', { n })}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            {(mode === 'groups_ko' || mode === 'partner_ko') && (
              <div className="field-row grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label={t('Gruppengröße')} {...(mode === 'partner_ko' ? { hint: t('mindestens — wer übrig bleibt, kommt in eine der Gruppen dazu') } : {})}>
                  <Select value={opts.groupSize} disabled={locked} onChange={(e) => setOpts({ ...opts, groupSize: Number(e.target.value) })}>
                    {groupSizeChoices(mode).map((n) => (
                      <option key={n} value={n}>
                        {mode === 'partner_ko' ? t('{n} Spieler', { n }) : t('{n} Teams', { n })}
                      </option>
                    ))}
                  </Select>
                </Field>
                {mode === 'partner_ko' && (
                  <Field label={t('Partner')} hint={t('Standard: jedes Paar spielt genau einmal zusammen. Viel länger: jedes Paar gegen jedes andere Paar („jeder mit jedem gegen jeden mit jedem“).')}>
                    <Select value={opts.partners} disabled={locked} onChange={(e) => setOpts({ ...opts, partners: e.target.value === 'all' ? 'all' : 'once' })} data-partner-modus>
                      <option value="once">{t('jedes Paar einmal zusammen ({n} Spiele bei 5)', { n: partnerMatchCount(5, 'once') })}</option>
                      <option value="all">{t('jedes Paar gegen jedes Paar ({n} Spiele bei 5)', { n: partnerMatchCount(5, 'all') })}</option>
                    </Select>
                  </Field>
                )}
                <Field label={t('Weiter je Gruppe')}>
                  <Select
                    value={opts.qualifiersPerGroup}
                    disabled={!qualiOffen}
                    onChange={(e) => setzeQuali({ qualifiersPerGroup: Number(e.target.value) }, { qualifiersPerGroup: Number(e.target.value) })}
                    data-weiter-je-gruppe
                  >
                    {[1, 2, 3].map((n) => (
                      <option key={n} value={n}>
                        {t('Top {n}', { n })}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label={t('Beste Nächstplatzierte')}>
                  <Select
                    value={opts.bestRemaining}
                    disabled={!qualiOffen}
                    onChange={(e) => setzeQuali({ bestRemaining: Number(e.target.value) }, { bestRemaining: Number(e.target.value) })}
                    data-beste-naechste
                  >
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                      <option key={n} value={n}>
                        {n === 0 ? t('keine') : `+${n}`}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
            )}
            <div className="field-row grid grid-cols-1 sm:grid-cols-2 gap-3">
              {mode !== 'ko' && mode !== 'club_rr' && (
                <Field label={t('Vereinskollegen trennen')}>
                  <Select value={opts.avoidSameClub ? '1' : '0'} disabled={locked} onChange={(e) => setOpts({ ...opts, avoidSameClub: e.target.value === '1' })}>
                    <option value="1">{t('ja, wenn möglich')}</option>
                    <option value="0">{t('egal')}</option>
                  </Select>
                </Field>
              )}
              {mode === 'club_rr' && (
                <Field
                  label={t('Gruppen')}
                  hint={t(
                    'Standard: alle in einer Gruppe, jedes Paar gegen alle Paare der anderen Mannschaften. Leistungsgruppen: „Ceva 1“, „Nucetto 1“, „Mombarcaro 1“ … spielen in Gruppe 1 jeder gegen jeden, die Zweier in Gruppe 2 — wie Medenspiele im Tennis. Die Nummer meldet der Teamleiter der Mannschaft.',
                  )}
                >
                  <Select value={opts.leistungsgruppen ? '1' : '0'} disabled={locked} onChange={(e) => setOpts({ ...opts, leistungsgruppen: e.target.value === '1' })}>
                    <option value="0">{t('eine Gruppe: jedes Paar gegen alle anderen Mannschaften')}</option>
                    <option value="1">{t('Leistungsgruppen: gleiche Nummer spielt gegeneinander')}</option>
                  </Select>
                </Field>
              )}
              {mode !== 'roundrobin' && mode !== 'club_rr' && (
                <Field label={t('Spiel um Platz 3')}>
                  <Select value={opts.thirdPlaceMatch ? '1' : '0'} disabled={!platz3Offen} onChange={(e) => setzePlatz3(e.target.value === '1')} data-platz3>
                    <option value="1">{t('ja')}</option>
                    <option value="0">{t('nein')}</option>
                  </Select>
                </Field>
              )}
            </div>
          </div>
          <Card title={t('Vorschau')}>
            <div className="p-4 font-mono text-[12px] text-muted leading-relaxed">
              <div className="label mb-1">{t('{teams} Teams · {courts} Felder', { teams: teamCount || t('angenommen 16'), courts: tournament.courts.filter((c) => c.active).length })}</div>
              {preview.lines.map((l) => (
                <div key={l}>{l}</div>
              ))}
              <div className="text-ink mt-1">{t('Gesamt ca. {d}', { d: fmtMinutes(preview.minutes) })}</div>
              <div className="mt-3 label">{t('Aktuelle Phasen')}</div>
              {comp.phases.map((p) => (
                <div key={p.id}>
                  {p.order}. {t(phaseLabel(p.type))} · {t(PHASE_STATUS[p.status] ?? p.status)}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </section>

      <section hidden={tab !== 'regeln'}>
        <SectionHead title={t('Regeln')} meta={locked ? t('Änderungen gelten für neue Ergebnisse') : undefined}>
          {rulesDirty && (
            <>
              <Button variant="ghost" onClick={() => setRules(comp.ruleSet)}>
                {t('Verwerfen')}
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  dispatch({ type: 'competition.updateRules', competitionId: comp.id, patch: rules });
                }}
              >
                {t('Regeln speichern')}
              </Button>
            </>
          )}
        </SectionHead>
        <RuleEditor rules={rules} onChange={setRules} />
      </section>
    </div>
  );
}
